import { Box, Stack, ToggleButton } from '@mui/material';
import { Bullet, FormikPoolDatePicker, PoolSelect, SelectOption } from '@synergy/components';
import { Component } from 'react';
import { Field, FieldProps, FormikProps } from 'formik';
import { FormattedDateTimeRange, FormattedMessage } from 'react-intl';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolRow, PoolTypography } from '@synergy/components-next';
import { ToggleButtonGroup } from '@mui/material';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { deliveryDateTypeOrder, getDeliveryDateTypeTranslationId } from '@synergy/models';
import dayjs from 'dayjs';

import { ConverterCustomer } from '../models/internal/converter-customer';
import { ConverterJob } from '../models/internal/converterJob';
import { TransitionTarget } from '../models/internal/transition-target';
import { getTranslationIdByTarget } from '../util/get-translation-id-by-target';

const styles = () =>
    createStyles({
        // Flush with the documents and positions below: 88 pixels in from
        // either side, the two choices stood out of line with everything else.
        container: {
            padding: 0,
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'stretch',
            gap: '8px',
        },
        dropdown: {
            width: '100%',
        },
    });

interface ConvertReceiptHeaderProps extends WithStyles<typeof styles> {
    possibleTargets: TransitionTarget[];
    converterCustomers: ConverterCustomer[];
    deliveryDateRange?: { from: Date; to: Date };
}
export const ConvertReceiptHeader = withStyles(styles)(
    class ConvertReceiptHeaderBase extends Component<ConvertReceiptHeaderProps> {
        private getReceiptTypeOptions = (): SelectOption[] => {
            const options = this.props.possibleTargets.map(target => ({
                value: target,
                displayValue: <FormattedMessage id={getTranslationIdByTarget(target)} />,
            }));
            return options;
        };

        // TODO: implement in future iterations
        private getCustomerOptions = (): SelectOption[] => {
            const dropdownCustomers = this.props.converterCustomers.map(customer => ({
                value: customer.id,
                displayValue:
                    this.props.converterCustomers.length > 1 ? (
                        <span>
                            {customer.name}
                            <Bullet />
                            {customer.deliveryAddress.title && (
                                <>
                                    {customer.deliveryAddress.title} <Bullet />
                                </>
                            )}
                            {[
                                customer.deliveryAddress.street,
                                customer.deliveryAddress.number,
                                customer.deliveryAddress.addition,
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            <Bullet />
                            {customer.deliveryAddress.city}
                        </span>
                    ) : (
                        <span>{customer.name}</span>
                    ),
            }));

            // Show all customers only if there are multiple customers
            if (this.props.converterCustomers.length > 1) {
                dropdownCustomers.unshift({
                    value: -1,
                    displayValue: <FormattedMessage id={'app.convertReceipt.customersAll'} />,
                });
            }
            return dropdownCustomers;
        };

        private handleTargetChanged = (form: FormikProps<ConverterJob>, name: string) => (value?: string) => {
            if (value) {
                form.setFieldValue(name, value);
            }
        };

        private handleCustomerSelected = (form: FormikProps<ConverterJob>, name: string) => (value?: string) => {
            if (value) {
                form.setFieldValue(name, value);
            }
        };

        public render() {
            const { classes } = this.props;

            const receiptTypes = this.getReceiptTypeOptions();
            const customerOptions = this.getCustomerOptions();

            return (
                <Stack gap={1}>
                    {this.props.deliveryDateRange && (
                        <PoolTypography
                            component={Box}
                            sx={{ mt: 3 }}
                            translationId="app.common.deliveryDateRange"
                            values={{
                                value: (
                                    <FormattedDateTimeRange
                                        from={this.props.deliveryDateRange.from}
                                        to={this.props.deliveryDateRange.to}
                                    />
                                ),
                            }}
                        />
                    )}
                    <div className={classes.container}>
                        <div className={classes.dropdown}>
                            <Field name="selectedTarget">
                                {({ field, form }: FieldProps<ConverterJob>) => (
                                    <PoolSelect
                                        fullWidth={true}
                                        label={<FormattedMessage id={'app.convertReceipt.receiptType'} />}
                                        options={receiptTypes}
                                        disallowDefaultValue={true}
                                        value={field.value}
                                        onValueChange={this.handleTargetChanged(form, field.name)}
                                        name={field.name}
                                    />
                                )}
                            </Field>
                        </div>
                        {/* No "Hinzufügen zu" in the other branch: it was a
                            permanently disabled select over an empty list, so it
                            only took up a slot in the dialog (#2184). */}
                        {this.props.deliveryDateRange && (
                            <PaloNextThemeProvider>
                                <PoolRow gap={1} sx={{ width: '100%' }}>
                                    <Field name="deliveryDateType">
                                        {({ field, form }: FieldProps<string>) => (
                                            <ToggleButtonGroup
                                                value={field.value}
                                                exclusive={true}
                                                onChange={(_, value) => form.setFieldValue(field.name, value)}
                                            >
                                                {deliveryDateTypeOrder.map(type => (
                                                    <ToggleButton key={type} value={type}>
                                                        <PoolTypography
                                                            variant="caption"
                                                            translationId={getDeliveryDateTypeTranslationId(type)}
                                                        />
                                                    </ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        )}
                                    </Field>
                                    <FormikPoolDatePicker
                                        name="deliveryDate"
                                        label={<FormattedMessage id="app.label.deliveryDate" />}
                                        disablePast={true}
                                        required={true}
                                    />
                                </PoolRow>
                            </PaloNextThemeProvider>
                        )}
                        <div className={classes.dropdown}>
                            <Field name="selectedCustomerId">
                                {({ field, form }: FieldProps<ConverterJob>) => (
                                    <PoolSelect
                                        fullWidth={true}
                                        label={<FormattedMessage id={'app.label.customer'} />}
                                        options={customerOptions}
                                        disallowDefaultValue={true}
                                        value={field.value}
                                        onValueChange={this.handleCustomerSelected(form, field.name)}
                                        name={field.name}
                                    />
                                )}
                            </Field>
                        </div>
                    </div>
                </Stack>
            );
        }
    }
);
